import PropTypes from 'prop-types';
import React from 'react';
import Select, { components } from 'react-select';
import { mergeSelectStyles } from './darkSelectStyles';

const selectStyles = mergeSelectStyles({
  multiValue: (base) => ({ ...base, backgroundColor: 'var(--sl-bg-secondary-hover, #e6e6e6)' }),
});

const CustomGroupHeading = (props) => {
  const { data } = props;
  const options = props.selectProps.options || [];
  const currentIndex = options.indexOf(data);
  
  const previousOption = currentIndex > 0 ? options[currentIndex - 1] : null;
  const previousIsSingleRoom = previousOption && !previousOption.options;
  
  const showTopBorder = previousIsSingleRoom;
  
  return (
    <div>
      {showTopBorder && <div style={{ borderTop: '1px solid var(--sl-border-secondary, #E9EAEB)' }} />}
      <components.GroupHeading
        {...props}
        style={{
          fontSize: '12px',
          fontWeight: 'normal',
          color: 'var(--sl-text-placeholder, #717680)',
          paddingLeft: '9px',
          paddingTop: '8px',
          paddingBottom: '4px',
          marginTop: '0px',
          marginBottom: '0px',
          textTransform: 'none'
        }}
      />
    </div>
  );
};

const CustomGroup = (props) => {
  const { data } = props;
  const options = props.selectProps.options || [];
  const currentIndex = options.indexOf(data);
  
  const nextOption = currentIndex < options.length - 1 ? options[currentIndex + 1] : null;
  const nextIsSingleRoom = nextOption && !nextOption.options;
  const nextIsGroup = nextOption && nextOption.options;
  
  const showBottomBorder = nextIsSingleRoom || nextIsGroup;
  
  return (
    <div>
      <components.Group {...props} />
      {showBottomBorder && <div style={{ borderBottom: '1px solid var(--sl-border-secondary, #E9EAEB)', marginTop: '4px' }} />}
    </div>
  );
};

const RoomSelector = ({
  rooms,
  venueOwnerships,
  selectedRooms,
  onChange,
  placeholder = "Select rooms...",
  isDisabled = false,
  menuPortalTarget = null,
  id = "room-selector",
  className = "",
  hasError = false,
  onAddVenue = null,
  ...otherProps
}) => {
  const AddVenueMenuList = (menuListProps) => (
    <components.MenuList {...menuListProps}>
      {menuListProps.children}
      <button
        type="button"
        data-testid="room-selector-add-venue"
        className="btn btn-link btn-sm text-left w-100"
        style={{ borderTop: '1px solid var(--sl-border-secondary, #E9EAEB)', padding: '8px 12px' }}
        onMouseDown={(e) => e.preventDefault()}
        onClick={() => onAddVenue()}
      >
        <i className="far fa-plus" aria-hidden="true" /> Add a new venue
      </button>
    </components.MenuList>
  );

  const selectComponents = {
    GroupHeading: CustomGroupHeading,
    Group: CustomGroup,
    ...(onAddVenue ? { MenuList: AddVenueMenuList } : {}),
  };

  const createRoomOptions = () => {
    if (!rooms || !venueOwnerships) return [];
    
    const venueOwnershipGroups = {};
    
    rooms.forEach(room => {
      if (!venueOwnershipGroups[room.venue_ownership_id]) {
        venueOwnershipGroups[room.venue_ownership_id] = [];
      }
      venueOwnershipGroups[room.venue_ownership_id].push(room);
    });
    
    const options = [];
    
    const sortedVenueOwnerships = venueOwnerships
      .filter(vo => venueOwnershipGroups[vo.id])
      .sort((a, b) => a.name.localeCompare(b.name));
    
    sortedVenueOwnerships.forEach(venueOwnership => {
      const roomsForVenueOwnership = venueOwnershipGroups[venueOwnership.id]
        .sort((a, b) => a.name.localeCompare(b.name));
      
      if (roomsForVenueOwnership.length === 1) {
        const room = roomsForVenueOwnership[0];
        options.push({
          value: room.id,
          label: room.name,
          room: room,
          venueOwnershipId: venueOwnership.id
        });
      } else {
        options.push({
          label: venueOwnership.name,
          options: roomsForVenueOwnership.map(room => ({
            value: room.id,
            label: room.name,
            room: room,
            venueOwnershipId: venueOwnership.id
          }))
        });
      }
    });
    
    return options;
  };

  const errorStyles = hasError ? {
    ...selectStyles,
    control: (base, state) => ({
      ...selectStyles.control(base, state),
      border: '2px solid #FF595E',
      '&:hover': {
        border: '2px solid #FF595E'
      }
    })
  } : selectStyles;

  return (
    <Select 
      key={`room-select-${selectedRooms.length}-${selectedRooms.map(r => r.value).join('-')}`}
      options={createRoomOptions()}
      id={id}
      className={className}
      styles={errorStyles}
      components={selectComponents}
      menuPortalTarget={menuPortalTarget}
      isMulti
      isSearchable
      isDisabled={isDisabled}
      placeholder={placeholder}
      value={selectedRooms}
      isOptionDisabled={(option) => {
        if (selectedRooms.length === 0) return false;
        
        const selectedVenueOwnershipId = selectedRooms[0].venueOwnershipId;
        
        if (option.options) {
          return !option.options.some(subOption => subOption.venueOwnershipId === selectedVenueOwnershipId);
        }
        
        return option.venueOwnershipId !== selectedVenueOwnershipId;
      }}
      onChange={onChange}
      {...otherProps}
    />
  );
};

RoomSelector.propTypes = {
  rooms: PropTypes.array.isRequired,
  venueOwnerships: PropTypes.array.isRequired,
  selectedRooms: PropTypes.array.isRequired,
  onChange: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
  isDisabled: PropTypes.bool,
  menuPortalTarget: PropTypes.object,
  id: PropTypes.string,
  className: PropTypes.string,
  hasError: PropTypes.bool,
  onAddVenue: PropTypes.func
};

export default RoomSelector;
